/**
 * Slice for storing user's reward activities
 */

import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { RewardActivity } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

export interface IRewardActivitiesState {
  activities?: RewardActivity[];
  getActivitesApiStatus: ApiCallResultStatus;
}

export const getAllActiveRewardActivitiesAsync = createOneOffAsyncApiThunk(
  'rewardActivities/getAllActiveRewardActivitiesAsync',
  () => microsoftConsumerSecurityClient.rewardActivities.getAllActiveRewardActivities(),
  ({ rewardActivities }) => rewardActivities.getActivitesApiStatus
);

export const handleGetAllActiveRewardActivitiesAsync = (builder: ActionReducerMapBuilder<IRewardActivitiesState>): void => {
  builder
    .addCase(getAllActiveRewardActivitiesAsync.pending, (state) => {
      state.getActivitesApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getAllActiveRewardActivitiesAsync.fulfilled, (state, { payload }) => {
      state.activities = payload;
      state.getActivitesApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(getAllActiveRewardActivitiesAsync.rejected, (state) => {
      state.getActivitesApiStatus = ApiCallResultStatus.FAILED;
    });
};

export const dismissRewardActivityAsync = createAsyncApiThunk(
  'rewardActivities/dismissRewardActivityAsync',
  ({ campaignId }: { campaignId: string }) => microsoftConsumerSecurityClient.rewardActivities.dismissRewardActivity(campaignId)
);

export const handleDismissRewardActivityAsync = (builder: ActionReducerMapBuilder<IRewardActivitiesState>): void => {
  builder
    .addCase(dismissRewardActivityAsync.pending, (state, { meta }) => {
      state.getActivitesApiStatus = ApiCallResultStatus.LOADING;

      // remove activity from store optimistically
      const { campaignId } = meta.arg;
      const index = state.activities?.findIndex((activity) => activity.campaignId === campaignId) ?? -1;
      if (index > -1) {
        state.activities?.splice(index, 1);
      }
    })
    .addCase(dismissRewardActivityAsync.fulfilled, (state) => {
      state.getActivitesApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(dismissRewardActivityAsync.rejected, (state) => {
      state.getActivitesApiStatus = ApiCallResultStatus.FAILED;
    });
};

function getDefaultRewardActivitiesState(): IRewardActivitiesState {
  return {
    getActivitesApiStatus: ApiCallResultStatus.DEFAULT,
  };
}

export const rewardActivitySlice = createSlice({
  name: 'rewardActivities',
  initialState: getDefaultRewardActivitiesState(),
  reducers: {},
  extraReducers: (builder) => {
    handleGetAllActiveRewardActivitiesAsync(builder);
    handleDismissRewardActivityAsync(builder);
  },
});

export default rewardActivitySlice.reducer;
